import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminShellService } from '../../admin/services/admin-shell.service';
import { LoaderService } from '../../shared/services/loader-service/loader.service';

const REVEAL_MS = 700;
const LEAVE_MS = 220;

@Component({
    selector: 'app-loader',
    standalone: true,
    templateUrl: './loader.component.html',
    styleUrl: './loader.component.scss',
})
export class LoaderComponent {
    readonly adminOpen = inject(AdminShellService).isOpen;
    readonly loading = signal(false);
    readonly leaving = signal(false);
    readonly looping = signal(false);
    private revealedAt = 0;
    private hideTimer = 0;
    private loopTimer = 0;

    constructor() {
        inject(DestroyRef).onDestroy(() => {
            clearTimeout(this.hideTimer);
            clearTimeout(this.loopTimer);
        });
        inject(LoaderService)
            .loadState.pipe(takeUntilDestroyed())
            .subscribe((isLoading) => {
                // Defer so hide/show from HTTP finalize does not run in the same CD pass (NG0100).
                queueMicrotask(() => (isLoading ? this.begin() : this.finish()));
            });
    }

    private begin(): void {
        clearTimeout(this.hideTimer);
        clearTimeout(this.loopTimer);
        this.hideTimer = 0;
        this.loopTimer = 0;
        this.leaving.set(false);
        this.looping.set(false);
        if (!this.loading()) {
            this.revealedAt = performance.now();
        }
        this.loading.set(true);
        if (this.motionReduced()) {
            return;
        }
        this.loopTimer = window.setTimeout(() => {
            this.loopTimer = 0;
            if (this.loading() && !this.leaving()) {
                this.looping.set(true);
            }
        }, REVEAL_MS);
    }

    private finish(): void {
        if (!this.loading() || this.leaving() || this.hideTimer) return;
        clearTimeout(this.loopTimer);
        this.loopTimer = 0;
        const elapsed = performance.now() - this.revealedAt;
        const remaining = this.motionReduced() ? 0 : Math.max(0, REVEAL_MS - elapsed);
        this.hideTimer = window.setTimeout(() => this.leave(), remaining);
    }

    private leave(): void {
        if (!this.loading()) return;
        this.leaving.set(true);
        this.hideTimer = window.setTimeout(() => {
            this.hideTimer = 0;
            this.leaving.set(false);
            this.loading.set(false);
        }, LEAVE_MS);
    }

    private motionReduced(): boolean {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
}
